import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useMetaPixel } from '../../contexts/MetaPixelContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import InternalNavbar from '../../components/InternalNavbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import subscriptionService from '../../services/subscriptionService';
import { FaGift, FaCheck, FaCrown, FaArrowRight } from 'react-icons/fa';
import './style.css';

const DemoConfirmationPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { subscription, isExpired, loading: subscriptionLoading, refreshStatus } = useSubscription();
    const { trackDemoActivated, trackButtonClick, trackError } = useAnalytics();
    const { trackStartTrial } = useMetaPixel();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Check if user has an expired demo and redirect BEFORE rendering
    useEffect(() => {
        if (!subscriptionLoading) {
            // Check if user has expired demo (even if subscription is null)
            if (isExpired) {
                console.log('User has expired demo, redirecting to demo-expired page');
                toast.info(t('demoConfirmation.demoTrialEnded'));
                navigate('/demo-expired', { replace: true });
                return;
            }

            // If user has an active subscription, redirect to dashboard
            if (subscription?.status === 'active') {
                console.log('User has active subscription, redirecting to dashboard');
                toast.info(t('demoConfirmation.alreadyActiveSubscription'));
                navigate('/dashboard', { replace: true });
                return;
            }
        }
    }, [subscription, isExpired, subscriptionLoading, navigate, t]);

    // Show loading while checking subscription status
    if (subscriptionLoading) {
        return null;
    }

    // Don't render the page if user has expired demo or active subscription
    if (isExpired || subscription?.status === 'active') {
        return null;
    }

    const handleActivateDemo = async () => {
        if (!user) {
            toast.error(t('demoConfirmation.loginRequired'));
            navigate('/login');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Track button click
            trackButtonClick('activate_demo', {
                user_id: user.id,
            });

            await subscriptionService.activateDemo();
            await refreshStatus();

            // Track successful demo activation
            trackDemoActivated(user.id);
            // Meta Pixel: Track StartTrial for ads attribution
            trackStartTrial({ predictedLtv: 49.00 });

            toast.success(t('demoConfirmation.congratsDemo'));

            // Show success message before redirect
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);
        } catch (err) {
            console.error('Error activating demo:', err);

            // Check error code
            const errorCode = err.response?.data?.detail?.code || err.response?.data?.error?.code;
            const errorMsg = err.response?.data?.detail?.message ||
                err.response?.data?.error?.message ||
                err.response?.data?.message ||
                t('demoConfirmation.activationFailed');

            // Track error
            trackError('demo_activation_failed', errorMsg, {
                error_code: errorCode,
                user_id: user.id,
            });

            // Handle different error cases
            if (errorCode === 'DEMO_ALREADY_USED') {
                toast.error(t('demoConfirmation.demoAlreadyUsed'));
                navigate('/demo-expired');
                return;
            } else if (errorCode === 'SUBSCRIPTION_EXISTS') {
                toast.info(t('demoConfirmation.alreadyActiveSubscription'));
                navigate('/dashboard');
                return;
            }

            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="demo-confirmation-page">
            <InternalNavbar />

            {/* Main Conversion Section */}
            <div className="demo-conversion-section">
                <div className="container">

                    {/* Hero Title */}
                    <div className="demo-conversion-hero-title">
                        <h1>{t('demoConfirmation.pageTitle')}</h1>
                        <h2>{t('demoConfirmation.pageSubtitle')}</h2>
                    </div>

                    {error && (
                        <div className="alert alert-warning demo-alert">
                            <FaCheck /> {error}
                        </div>
                    )}

                    {/* Horizontal Layout */}
                    <div className="row align-items-center demo-conversion-row">

                        {/* Left Side - Icon Display */}
                        <div className="col-lg-5">
                            <div className="demo-icon-display">
                                <div className="icon-circle">
                                    <FaGift />
                                </div>
                                <h3>{t('demoConfirmation.freeLabel')}</h3>
                                <p>{t('demoConfirmation.noCreditCard')}</p>
                            </div>
                        </div>

                        {/* Right Side - Content */}
                        <div className="col-lg-7">
                            <div className="demo-conversion-content">

                                {/* Benefits List */}
                                <div className="demo-benefits-list">
                                    <div className="demo-benefit-item">
                                        <FaCheck className="benefit-icon" />
                                        <p>{t('demoConfirmation.benefit1')}</p>
                                    </div>
                                    <div className="demo-benefit-item">
                                        <FaCheck className="benefit-icon" />
                                        <p>{t('demoConfirmation.benefit2')}</p>
                                    </div>
                                    <div className="demo-benefit-item">
                                        <FaCheck className="benefit-icon" />
                                        <p>{t('demoConfirmation.benefit3')}</p>
                                    </div>
                                </div>

                                {/* Bonus Reminder */}
                                <div className="demo-bonus-reminder">
                                    <div className="bonus-header">
                                        <FaCrown className="bonus-crown" />
                                        <h3>{t('demoConfirmation.beforeProceeding')}</h3>
                                    </div>
                                    <div className="bonus-content">
                                        <p className="bonus-text">{t('demoConfirmation.rememberUnlock')}</p>
                                        <p className="bonus-highlight">{t('demoConfirmation.free2Months')}</p>
                                    </div>
                                </div>

                                {/* CTA Button */}
                                <button
                                    className="btn-proceed-demo"
                                    onClick={handleActivateDemo}
                                    disabled={loading}
                                >
                                    {loading ? t('demoConfirmation.activatingDemo') : (
                                        <>
                                            {t('demoConfirmation.proceedToDemo')} <FaArrowRight />
                                        </>
                                    )}
                                </button>

                                {user && (
                                    <div className="user-info-minimal">
                                        <p>{t('demoConfirmation.loggedInAs')} <strong>{user.email}</strong></p>
                                    </div>
                                )}

                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <Footer />
            <Scrollbar />
        </div>
    );
};

export default DemoConfirmationPage;
